/**
 * SequenceVoiturette.jsx — Page générique d'une séquence AM Voiturette.
 *
 * Rendue par 5 routes différentes (sequence-1..5) qui ne changent qu'un
 * paramètre `slug`. On lit la data correspondante et on rend :
 *   - Header séquence (kicker + durée + titre)
 *   - Bloc RefOfficiel avec le contenu réglementaire strict
 *   - Bloc RefEnrichi Code 2 Roues (QCM, scénarios, check-list…)
 *   - Navigation séquence précédente / suivante
 *
 * Pour la séquence 5 (Sensibilisation), on route vers le renderer riche
 * de la Sensibilisation (accroche, cards, scénarios, checklist).
 */
import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Home, Clock, CheckCircle2, AlertTriangle,
  Info, HelpCircle, Sparkles, ChevronRight, ShieldAlert, QrCode,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useSeo from '../../../hooks/useSeo';
import { SEQUENCES, findSequenceBySlug, FORMATION_META } from '../../../data/cours/permisAmVoiturette/referentiel';
import { SEQUENCES_DATA } from '../../../data/cours/permisAmVoiturette';
import { RefOfficiel, RefEnrichi, RefPair, RefList } from '../../../components/RefBlock';
import { ProjectorToggle } from '../../../components/ProjectorToggle';
import SensibilisationVoiturette from './SensibilisationVoiturette';
import { VISUELS } from '../../../data/cours/permisAmVoiturette/visuels';
import { VisuelCard } from '../../../components/voiturette/VisuelCard';
import LivretQrShareModal from '../../../components/livrets/LivretQrShareModal';

export default function SequenceVoiturette() {
  const { slug } = useParams();
  const meta = findSequenceBySlug(slug);
  const data = SEQUENCES_DATA[slug];
  const [qrOpen, setQrOpen] = useState(false);

  useSeo({
    title: meta ? `${meta.kicker} · ${meta.shortTitle} — Voiturette` : 'Séquence AM Voiturette',
    description: meta?.subtitle || '',
  });

  if (!meta || !data) {
    return <Navigate to="/permis-am/voiturette" replace />;
  }

  // La séquence 5 utilise un renderer dédié riche (basé sur la trame cyclo)
  if (slug === 'sensibilisation') {
    return <SensibilisationVoiturette meta={meta} data={data} />;
  }

  const idxInList = SEQUENCES.findIndex((s) => s.slug === slug);
  const prevSeq = idxInList > 0 ? SEQUENCES[idxInList - 1] : null;
  const nextSeq = idxInList < SEQUENCES.length - 1 ? SEQUENCES[idxInList + 1] : null;

  return (
    <div className="min-h-screen bg-background text-foreground pb-20" data-testid={`voiturette-${slug}`}>
      {/* Sticky topbar — compacte mobile, largeur maîtrisée desktop */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="outline" size="sm" data-testid="btn-hub-voiturette">
            <Link to="/permis-am/voiturette" aria-label="Retour au Hub Voiturette">
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Hub Voiturette</span>
            </Link>
          </Button>
          <div className="flex-1 text-center min-w-0">
            <div
              className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] truncate whitespace-nowrap"
              style={{ color: meta.color }}
              title={`${meta.kicker} · ${meta.duree}`}
            >
              {meta.kicker} · {meta.duree}
            </div>
          </div>
          <ProjectorToggle testid={`proj-sequence-${slug}`} />
          <Button
            size="sm"
            onClick={() => setQrOpen(true)}
            className="bg-rose-500 hover:bg-rose-400 text-white font-black uppercase tracking-wider shadow-lg"
            data-testid="btn-qr-sequence"
            title="Partager cette séquence par QR code"
          >
            <QrCode className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">QR élève</span>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/" aria-label="Retour à l'accueil">
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Accueil</span>
            </Link>
          </Button>
        </div>
      </div>

      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 space-y-6 lg:space-y-10 scroll-mt-24">
        <header className="mb-6 lg:mb-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-3" style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}>
            {meta.kicker} · {meta.duree}
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight break-words" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {meta.title}
          </h1>
          <p className="text-white/70 mt-3 text-base sm:text-lg lg:text-xl leading-relaxed max-w-3xl">{meta.subtitle}</p>
        </header>

        {/* Poster séquence — visuel Code 2 Roues distinctif */}
        {meta.poster && (
          <div className="rounded-2xl overflow-hidden border-2 shadow-2xl" style={{ borderColor: `${meta.color}55`, boxShadow: `0 20px 60px -20px ${meta.color}44` }}>
            <img
              src={meta.poster}
              alt={meta.posterAlt || meta.title}
              className="w-full h-auto block"
              loading="eager"
              data-testid={`sequence-poster-${slug}`}
            />
          </div>
        )}

        {/* RENDU par séquence */}
        {slug === 'representations' && <Sequence1Content data={data} />}
        {slug === 'hors-circulation' && <Sequence2Content data={data} />}
        {slug === 'code' && <Sequence3Content data={data} />}
        {slug === 'circulation' && <Sequence4Content data={data} />}

        {/* Navigation */}
        <div className="pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
          <div>
            {prevSeq && (
              <Button asChild variant="outline" size="sm" data-testid="btn-prev-seq">
                <Link to={`/permis-am/voiturette/${prevSeq.slug}`}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> {prevSeq.kicker} · {prevSeq.shortTitle}
                </Link>
              </Button>
            )}
          </div>
          <div>
            {nextSeq && (
              <Button asChild className="bg-red-600 hover:bg-red-500 text-white" size="sm" data-testid="btn-next-seq">
                <Link to={`/permis-am/voiturette/${nextSeq.slug}`}>
                  {nextSeq.kicker} · {nextSeq.shortTitle} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </article>

      {/* Modale QR — partage direct de cette séquence à l'élève */}
      <LivretQrShareModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        livretPath={`/permis-am/voiturette/${slug}`}
        livretTitle={`${meta.kicker} · ${meta.shortTitle}`}
        livretSubtitle={`Permis AM Voiturette · ${meta.duree}`}
        accentColor={meta.color || '#f43f5e'}
        tabletMode
        bypassAuth
      />
    </div>
  );
}

/* ────────────────── SÉQUENCE 1 · Représentations ────────────────── */
function Sequence1Content({ data }) {
  return (
    <>
      <RefPair>
        <RefOfficiel title="Objectifs officiels" duration="30 min" testid="s1-officiel-obj">
          <RefList items={data.officiel.objectifs} />
        </RefOfficiel>
        <RefEnrichi title="Questionnaire d'accueil interactif" subtitle="Sers-t'en pour lancer l'échange">
          <p className="italic text-white/70">{data.enrichi.accroche}</p>
          <ol className="mt-2 space-y-2">
            {data.enrichi.questionnaire.map((q, i) => (
              <li key={i} className="rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="text-[11px] font-black uppercase tracking-widest text-red-300 mb-0.5">Question {i + 1}</div>
                <div className="text-white text-[13px]">{q.q}</div>
                {q.options && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {q.options.map((o, oi) => (
                      <span key={oi} className="text-[11px] px-2 py-0.5 rounded bg-white/[0.05] border border-white/10 text-white/70">{o}</span>
                    ))}
                  </div>
                )}
                {q.bonus && <p className="text-[11.5px] text-emerald-300/85 italic mt-1.5">💡 {q.bonus}</p>}
              </li>
            ))}
          </ol>
        </RefEnrichi>
      </RefPair>

      <RefOfficiel title="Thématiques à aborder obligatoirement">
        <div className="grid sm:grid-cols-2 gap-2">
          {data.officiel.thematiques.map((t) => (
            <div key={t.key} className="rounded-lg bg-sky-500/5 border border-sky-500/25 px-3 py-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-sky-300">{t.label}</div>
              <div className="text-white/80 text-[12.5px] mt-0.5">{t.desc}</div>
            </div>
          ))}
        </div>
      </RefOfficiel>

      <RefEnrichi title="Vrai / Faux — pour engager l'échange">
        <ul className="space-y-2">
          {data.enrichi.vraiFaux.map((vf, i) => (
            <li key={i} className="rounded-lg border border-white/10 bg-black/20 p-3">
              <div className="flex items-start gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${vf.reponse ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'}`}>
                  {vf.reponse ? 'Vrai' : 'Faux'}
                </span>
                <div className="flex-1">
                  <div className="text-white text-[13px] font-semibold">{vf.affirmation}</div>
                  <div className="text-white/70 text-[12px] mt-1 italic">{vf.explication}</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <p className="text-[12px] text-white/60 italic pt-2 border-t border-red-500/20">
          🎯 {data.enrichi.ouverture}
        </p>
      </RefEnrichi>
    </>
  );
}

/* ────────────────── SÉQUENCE 2 · Hors circulation ────────────────── */
function Sequence2Content({ data }) {
  const { equipements, decouverteVehicule, maitrise } = data.officiel;
  return (
    <>
      <RefOfficiel title="Équipements — Voiturette" duration="Adapté à l'option" testid="s2-off-equip">
        <p className="italic text-sky-100/80">{equipements.description}</p>
        <ul className="mt-2 space-y-1.5">
          {equipements.items.map((e) => (
            <li key={e.key} className="flex items-start gap-2">
              <span className="w-1 h-1 rounded-full bg-sky-300 shrink-0 mt-2" />
              <div>
                <strong className="text-white">{e.label}</strong>
                {e.flag === 'obligatoire' && <span className="ml-2 px-1.5 py-0.5 rounded bg-rose-500/25 border border-rose-500/40 text-[9.5px] font-black uppercase tracking-widest text-rose-200">Obligatoire</span>}
                <div className="text-white/70 text-[12.5px]">{e.desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </RefOfficiel>

      <RefPair>
        <RefOfficiel title="Découverte du véhicule">
          <p className="italic text-sky-100/80">{decouverteVehicule.description}</p>
          <RefList items={decouverteVehicule.items} />
        </RefOfficiel>
        <RefEnrichi title="Check-list AVANT DE PARTIR (Code 2 Roues)" subtitle="Cochable, imprimable, affichable dans l'auto-école">
          <ul className="space-y-1.5">
            {data.enrichi.checklistDepart.map((c) => (
              <li key={c.key} className="flex items-start gap-2">
                <div className="w-4 h-4 rounded border-2 border-white/25 mt-0.5 shrink-0" />
                <div>
                  <div className="text-white text-[13px] font-semibold">{c.label}</div>
                  <div className="text-white/65 text-[12px]">{c.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </RefEnrichi>
      </RefPair>

      <RefPair>
        <RefOfficiel title="Maîtrise du quadricycle">
          <p className="italic text-sky-100/80">{maitrise.description}</p>
          <RefList items={maitrise.items} />
        </RefOfficiel>
        <RefEnrichi title="4 niveaux de maîtrise — parcours élève">
          <ol className="space-y-2">
            {data.enrichi.niveauxMaitrise.map((n) => (
              <li key={n.niveau} className="flex items-start gap-2.5">
                <span className="w-7 h-7 rounded-full bg-red-500/25 border border-red-500/40 text-red-200 flex items-center justify-center text-[12px] font-black shrink-0">
                  {n.niveau}
                </span>
                <div>
                  <div className="text-white text-[13px] font-black">{n.label}</div>
                  <div className="text-white/70 text-[12.5px]">{n.desc}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-3 pt-3 border-t border-red-500/20">
            <div className="text-[10px] font-black uppercase tracking-widest text-red-300 mb-1">Focus voiturette</div>
            <ul className="space-y-1 text-white/75 text-[12.5px]">
              {data.enrichi.focusVoiturette.map((f, i) => <li key={i} className="flex gap-2"><Sparkles className="w-3 h-3 mt-1 text-red-400 shrink-0" /> {f}</li>)}
            </ul>
          </div>
        </RefEnrichi>
      </RefPair>

      {/* Visuel 5 — Maîtriser le véhicule hors circulation */}
      <VisuelCard visuel={VISUELS[5]} testid="s2-visuel-maitriser-vehicule" />
    </>
  );
}

/* ────────────────── SÉQUENCE 3 · Code ────────────────── */
function Sequence3Content({ data }) {
  return (
    <>
      <RefPair>
        <RefOfficiel title="Signalisation" duration="Verticale + horizontale">
          <p className="italic text-sky-100/80">{data.officiel.signalisation.description}</p>
          <div className="mt-2 grid gap-1.5">
            {data.officiel.signalisation.familles.map((f) => (
              <div key={f.key} className="rounded-lg bg-sky-500/5 border border-sky-500/25 px-3 py-1.5">
                <div className="text-[11px] font-black uppercase tracking-widest text-sky-300">{f.label}</div>
                <div className="text-white/80 text-[12.5px]">{f.desc}</div>
              </div>
            ))}
          </div>
        </RefOfficiel>
        <RefOfficiel title="Règles de circulation">
          <p className="italic text-sky-100/80">{data.officiel.regles.description}</p>
          <RefList items={data.officiel.regles.items} />
        </RefOfficiel>
      </RefPair>

      <RefEnrichi title="Mini-quiz de mise en situation (Code 2 Roues)">
        <ol className="space-y-3">
          {data.enrichi.miniQuizz.map((q, i) => (
            <li key={i} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-red-300 mb-1">Situation {i + 1}</div>
              <div className="text-white text-[13px] mb-1">{q.situation}</div>
              <div className="text-red-200 text-[13px] font-black">{q.question}</div>
              <ul className="mt-2 space-y-1">
                {q.options.map((o, oi) => (
                  <li key={oi} className={`px-3 py-1.5 rounded-lg border text-[12.5px] ${o.bon ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100' : 'border-white/10 bg-white/[0.02] text-white/70'}`}>
                    {o.bon && <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-300" />} {o.txt}
                  </li>
                ))}
              </ul>
              <p className="text-[11.5px] text-white/60 italic mt-2">🎯 {q.explication}</p>
            </li>
          ))}
        </ol>
        <div className="mt-4 pt-3 border-t border-red-500/20">
          <div className="text-[10px] font-black uppercase tracking-widest text-red-300 mb-1">Astuces voiturette</div>
          <ul className="space-y-1 text-white/80 text-[12.5px]">
            {data.enrichi.astucesVoiturette.map((a, i) => <li key={i} className="flex gap-2"><Info className="w-3 h-3 mt-1 text-red-400 shrink-0" /> {a}</li>)}
          </ul>
        </div>
      </RefEnrichi>
    </>
  );
}

/* ────────────────── SÉQUENCE 4 · Circulation ────────────────── */
function Sequence4Content({ data }) {
  return (
    <>
      {/* Visuel 7 — Observer et communiquer (exigence transversale) */}
      <VisuelCard visuel={VISUELS[7]} testid="s4-visuel-observer-communiquer" />

      <RefOfficiel title="4 exigences transversales" subtitle="À travailler dans TOUTES les compétences" duration="3 h min conduite">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {data.officiel.exigencesTransversales.map((e) => (
            <div key={e.key} className="rounded-lg bg-sky-500/10 border border-sky-500/30 px-3 py-2 text-center">
              <div className="text-[10px] font-black uppercase tracking-widest text-sky-200">{e.label}</div>
            </div>
          ))}
        </div>
      </RefOfficiel>

      <RefOfficiel title="10 compétences de conduite">
        <div className="space-y-2">
          {data.officiel.competences.map((c) => (
            <details key={c.id} className="rounded-lg bg-sky-500/5 border border-sky-500/25 p-3 group" data-testid={`s4-comp-${c.id}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-sky-500/25 border border-sky-500/40 text-sky-200 flex items-center justify-center text-[12px] font-black">{c.num}</span>
                  <div className="text-white font-black text-[14px]">{c.label}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/50 group-open:rotate-90 transition" />
              </summary>
              <div className="mt-3 pl-9 space-y-1.5 text-[12.5px] text-white/80">
                <RefList items={c.items} />
                {c.specifiqueVoiturette && (
                  <div className="mt-2 pt-2 border-t border-sky-500/25">
                    <div className="text-[10px] font-black uppercase tracking-widest text-sky-300 mb-1">Spécifique voiturette</div>
                    <RefList items={c.specifiqueVoiturette} />
                  </div>
                )}
                {c.enjeu && <p className="italic text-sky-100/70 pt-1">🎯 {c.enjeu}</p>}
                {/* Visuels contextuels par compétence */}
                {c.id === 'rechercher-indices' && (
                  <div className="mt-3"><VisuelCard visuel={VISUELS[11]} compact testid="s4-comp-visuel-meteo" /></div>
                )}
                {c.id === 'stationner' && (
                  <div className="mt-3"><VisuelCard visuel={VISUELS[6]} compact testid="s4-comp-visuel-stationner" /></div>
                )}
                {c.id === 'positionnement' && (
                  <div className="mt-3"><VisuelCard visuel={VISUELS[9]} compact testid="s4-comp-visuel-giratoires" /></div>
                )}
                {c.id === 'intersections' && (
                  <div className="mt-3"><VisuelCard visuel={VISUELS[8]} compact testid="s4-comp-visuel-intersections" /></div>
                )}
              </div>
            </details>
          ))}
        </div>
      </RefOfficiel>

      {/* Visuel 10 — Différence de vitesse (version détaillée) */}
      <VisuelCard visuel={VISUELS[10]} testid="s4-visuel-difference-vitesse" />

      <RefEnrichi title={data.enrichi.activite.titre} subtitle={data.enrichi.activite.description}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {data.enrichi.activite.questions.map((q, i) => (
            <div key={i} className="rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-center">
              <div className="text-2xl mb-1">{q.emoji}</div>
              <div className="text-red-100 text-[12px] font-semibold">{q.txt}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-red-500/20">
          <div className="text-[10px] font-black uppercase tracking-widest text-red-300 mb-1">Focus voiturette</div>
          <ul className="space-y-1 text-white/80 text-[12.5px]">
            {data.enrichi.focusVoiturette.map((f, i) => <li key={i} className="flex gap-2"><Sparkles className="w-3 h-3 mt-1 text-red-400 shrink-0" /> {f}</li>)}
          </ul>
        </div>
      </RefEnrichi>
    </>
  );
}
